import {
  ChevronDown,
  GitPullRequestArrow,
  Menu,
  Plus,
  Search,
  Ticket as TicketIcon,
} from 'lucide-react'
import type { ReactNode } from 'react'
import { useNavigate } from 'react-router-dom'

import { SignOutItem } from '../SignOutItem'
import { ThemeToggle } from './ThemeToggle'
import { Badge } from '../ui/badge'
import { Button } from '../ui/button'
import {
  Avatar,
  AvatarFallback,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
  Separator,
} from '../ui/primitives'

function initialsOf(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((part) => part[0]?.toUpperCase() ?? '')
    .join('')
}

export function TopNav({
  user,
  onOpenSearch,
  onOpenNav,
  scopeControl,
  notifications,
}: {
  user: { name: string; email: string; roles?: string[] }
  onOpenSearch: () => void
  onOpenNav: () => void
  /** Agent-console concepts, injected so this shell stays generic. */
  scopeControl?: ReactNode
  notifications?: ReactNode
}) {
  const navigate = useNavigate()

  return (
    <header className="sticky top-0 z-30 flex h-12 shrink-0 items-center gap-2 border-b border-border bg-background/95 px-3 backdrop-blur supports-[backdrop-filter]:bg-background/80">
      <Button
        variant="ghost"
        size="icon-sm"
        className="lg:hidden"
        onClick={onOpenNav}
        aria-label="Open navigation"
      >
        <Menu />
      </Button>

      {/* Search opens the palette rather than being an input itself, so there is
       *  one search surface and one keyboard shortcut, not two. */}
      <button
        type="button"
        onClick={onOpenSearch}
        className="flex h-8 flex-1 items-center gap-2 rounded-md border border-input bg-surface px-2.5 text-sm text-muted-foreground shadow-xs transition-colors hover:bg-accent sm:max-w-sm"
      >
        <Search className="size-4 shrink-0" />
        <span className="truncate">Search…</span>
        <kbd className="ml-auto hidden shrink-0 rounded border border-border bg-background px-1.5 py-0.5 font-mono text-xs sm:inline">
          ⌘K
        </kbd>
      </button>

      {scopeControl}

      <div className="ml-auto flex items-center gap-1">
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="gap-1">
              <Plus />
              <span className="hidden sm:inline">Create</span>
              <ChevronDown className="size-3 opacity-60" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end">
            <DropdownMenuLabel>Quick create</DropdownMenuLabel>
            <DropdownMenuItem onSelect={() => navigate('/new')}>
              <TicketIcon />
              Ticket
            </DropdownMenuItem>
            <DropdownMenuItem onSelect={() => navigate('/agent/changes')}>
              <GitPullRequestArrow />
              Change
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>

        {notifications}

        <ThemeToggle />

        <Separator orientation="vertical" className="mx-1 h-5" />

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="sm" className="gap-2 pl-1 pr-2">
              <Avatar className="size-6">
                <AvatarFallback>{initialsOf(user.name || user.email)}</AvatarFallback>
              </Avatar>
              <span className="hidden max-w-[8rem] truncate sm:inline">{user.name}</span>
              <ChevronDown className="size-3 opacity-60" />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-60">
            <div className="px-2 py-1.5">
              <p className="truncate text-sm font-medium">{user.name}</p>
              <p className="truncate text-xs text-muted-foreground">{user.email}</p>
              {user.roles && user.roles.length > 0 && (
                <div className="mt-1.5 flex flex-wrap gap-1">
                  {user.roles.map((role) => (
                    <Badge key={role} variant="neutral" size="sm">
                      {role}
                    </Badge>
                  ))}
                </div>
              )}
            </div>
            <DropdownMenuSeparator />
            <DropdownMenuItem onSelect={() => navigate('/')}>
              <TicketIcon />
              Switch to portal
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <SignOutItem />
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </header>
  )
}
